import type {
  BookingPolicies,
  DdPolicy,
  DndPolicy,
  OverweightPolicy,
  WeightLimitPolicy,
} from "@/types/api";
import type { RateCardPoliciesSnapshot, RateLineRow } from "@/types/rate";

const DD_EQUIPMENT_ALL = "*";

export const DD_POLICY_TYPE_LABELS: Record<string, string> = {
  destination_demurrage: "Destination demurrage",
  destination_detention: "Destination detention",
  origin_detention: "Origin detention",
  combined: "Combined D&D",
};

export function ddPolicyTypeLabel(type: string): string {
  return DD_POLICY_TYPE_LABELS[type] ?? type.replaceAll("_", " ");
}

export function hasBookingPolicies(
  policies: BookingPolicies | null | undefined,
): boolean {
  return (
    policies != null &&
    (policies.max_containers != null ||
      policies.min_containers != null ||
      policies.min_teu != null)
  );
}

export function countCardPolicies(
  card: RateCardPoliciesSnapshot,
  ddPolicies: DdPolicy[] = [],
): number {
  return (
    card.overweight_policies.length +
    card.weight_limit_policies.length +
    (hasBookingPolicies(card.booking_policies) ? 1 : 0) +
    Math.max(ddPolicies.length, card.dnd_policies.length)
  );
}

export function hasLineWeight(line: RateLineRow): boolean {
  return (
    line.max_weight != null ||
    line.max_gross_kg != null ||
    line.max_cargo_kg != null
  );
}

function equipmentMatches(
  sizes: string[] | undefined,
  types: string[] | undefined,
  equipmentSize: string | null | undefined,
  equipmentType: string | null | undefined,
): boolean {
  const size = equipmentSize?.trim();
  const type = equipmentType?.trim().toUpperCase();
  const sizeOk =
    !sizes ||
    sizes.length === 0 ||
    (size != null && sizes.includes(size));
  const typeOk =
    !types ||
    types.length === 0 ||
    (type != null && types.some((t) => t.toUpperCase() === type));
  return sizeOk && typeOk;
}

function originCountryMatches(
  include: string[] | undefined,
  exclude: string[] | undefined,
  originLocode: string | null | undefined,
): boolean {
  const country = originLocode?.trim().slice(0, 2).toUpperCase();
  if (
    include &&
    include.length > 0 &&
    (country == null || !include.map((c) => c.toUpperCase()).includes(country))
  ) {
    return false;
  }
  if (
    exclude &&
    exclude.length > 0 &&
    country != null &&
    exclude.map((c) => c.toUpperCase()).includes(country)
  ) {
    return false;
  }
  return true;
}

export function filterApplicableOverweightPolicies(
  policies: OverweightPolicy[],
  equipmentSize: string | null | undefined,
  equipmentType: string | null | undefined,
  originLocode?: string | null,
): OverweightPolicy[] {
  return policies.filter(
    (policy) =>
      equipmentMatches(
        policy.equipment_sizes,
        policy.equipment_types,
        equipmentSize,
        equipmentType,
      ) &&
      originCountryMatches(
        policy.origin_country_codes,
        policy.exclude_origin_country_codes,
        originLocode,
      ),
  );
}

export function filterApplicableWeightLimitPolicies(
  policies: WeightLimitPolicy[],
  equipmentSize: string | null | undefined,
  equipmentType: string | null | undefined,
  originLocode?: string | null,
): WeightLimitPolicy[] {
  return policies.filter(
    (policy) =>
      equipmentMatches(
        policy.equipment_sizes,
        policy.equipment_types,
        equipmentSize,
        equipmentType,
      ) &&
      originCountryMatches(
        policy.origin_country_codes,
        policy.exclude_origin_country_codes,
        originLocode,
      ),
  );
}

export function filterApplicableSpotDnd(
  policies: DndPolicy[],
  equipmentSize: string | null | undefined,
  equipmentType: string | null | undefined,
  destinationLocode?: string | null,
): DndPolicy[] {
  const size = equipmentSize?.trim();
  const type = equipmentType?.trim().toUpperCase();
  const dest = destinationLocode?.trim().toUpperCase();
  const token = [size, type].filter(Boolean).join("").toUpperCase();

  return policies.filter((policy) => {
    const scope = policy.container_size_type?.replace(/\s+/g, "").toUpperCase();
    const sizeOk = !scope || !token || scope.includes(size ?? "") || scope === token;
    const portOk =
      !policy.port_locode ||
      !dest ||
      policy.port_locode.toUpperCase() === dest;
    return sizeOk && portOk;
  });
}

export function policyRouteLabel(
  origin?: string | null,
  destination?: string | null,
): string {
  const from = origin?.trim() || "All origins";
  const to = destination?.trim() || "All destinations";
  return `${from} → ${to}`;
}

export function policyEquipmentLabel(
  sizes: string[] | undefined,
  types: string[] | undefined,
): string {
  const sizePart = sizes && sizes.length > 0 ? sizes.join(", ") : "";
  const typePart = types && types.length > 0 ? types.join(", ") : "";
  if (!sizePart && !typePart) return "All equipment";
  return [sizePart, typePart].filter(Boolean).join(" / ");
}

export function ddEquipmentScopeLabel(
  equipmentSize: string | null | undefined,
  equipmentType: string | null | undefined,
): string {
  const allSize = !equipmentSize || equipmentSize === DD_EQUIPMENT_ALL;
  const allType = !equipmentType || equipmentType === DD_EQUIPMENT_ALL;
  if (allSize && allType) return "All equipment";
  if (allSize) return `${equipmentType} · all sizes`;
  if (allType) return `${equipmentSize}' · all types`;
  return `${equipmentSize}' ${equipmentType}`;
}

export function formatKgAsTons(kg: number): string {
  const tons = kg / 1000;
  return Number.isInteger(tons) ? String(tons) : tons.toFixed(1);
}

/** Compact table label for a line's max-weight cap (e.g. `18t / TEU`). */
export function formatLineMaxWeight(line: {
  max_weight: number | null;
  weight_limit_unit?: string | null;
}): string | null {
  if (line.max_weight == null) return null;
  const unit =
    line.weight_limit_unit === "per_teu"
      ? " / TEU"
      : line.weight_limit_unit === "per_container"
        ? " / container"
        : "";
  return `${formatKgAsTons(line.max_weight)}t${unit}`;
}

export function formatOverweightStepRange(step: {
  min_kg: number;
  max_kg: number | null;
}): string {
  const min = formatKgAsTons(step.min_kg);
  if (step.max_kg != null) {
    return `${min}–${formatKgAsTons(step.max_kg)}t`;
  }
  return `${min}t+`;
}

export function formatOverweightSurchargeAmount(surcharge: number): string {
  return surcharge.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function formatOverweightStep(
  step: { min_kg: number; max_kg: number | null; surcharge: number },
  currency: string,
): string {
  const amount = formatOverweightSurchargeAmount(step.surcharge);
  return `${formatOverweightStepRange(step)} · ${currency} ${amount}`;
}

export function formatWeightLimit(policy: WeightLimitPolicy): string {
  const unit = policy.limit_unit === "per_teu" ? "TEU" : "container";
  return `${formatKgAsTons(policy.limit_kg)}t / ${unit} ${policy.weight_basis}`;
}

export function formatFreeTime(
  days: number | null | undefined,
  daysMax: number | null | undefined,
): string | null {
  if (days == null && daysMax == null) return null;
  if (days != null && daysMax != null && daysMax !== days) {
    return `${days}–${daysMax} days`;
  }
  const value = days ?? daysMax;
  return value == null ? null : `${value} ${value === 1 ? "day" : "days"}`;
}

/** Compact label for rate lookup / line headers, e.g. `Free days 14`. */
export function formatRateLineFreeDaysHeader(input: {
  free_time_days?: number | null;
  free_time_days_max?: number | null;
  dnd_policies?: { freetime_grant_days: number }[] | null;
}): string | null {
  const days = input.free_time_days;
  const daysMax = input.free_time_days_max;
  if (days != null || daysMax != null) {
    if (days != null && daysMax != null && daysMax !== days) {
      return `Free days ${days}–${daysMax}`;
    }
    const value = days ?? daysMax;
    return value != null ? `Free days ${value}` : null;
  }
  const pick = (input.dnd_policies ?? []).find(
    (policy) => policy.freetime_grant_days > 0,
  );
  return pick ? `Free days ${pick.freetime_grant_days}` : null;
}

